import { array } from '@disy/lib/array-util';
import { isEqual } from '@disy/lib/is-equal';

import type { DatasetKey, DatasetKeyValue } from 'cadenza/api-client/self-service-api';
import type { SetSelectionCommandPayload } from 'cadenza/api-client/workbook-command-payloads';
import { Command } from 'cadenza/api-client/workbook-commands';
import type { ObjecttypeId, ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { getStore } from 'cadenza/store/cadenza-store';
import { toHashCode } from 'cadenza/workbook/selection-cart/selection-cart-utils';
import type {
  ColumnIdentificationDto,
  ColumnOriginDto,
  QueryColumnOriginDto,
} from 'cadenza/workbook/selection/model/column-origin-dto';
import type { SelectionRecordsDto } from 'cadenza/workbook/selection/model/selection-records-dto';
import { executeCommand, fetchDataDependencyState } from 'cadenza/workbook/workbook-core-reducers.state';
import { getSelection } from 'cadenza/workbook/workbook-core-selectors.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';
import { invalidateDataViewsUsingObjecttypes } from 'cadenza/workbook/workbook.state';

/**
 * Creates a comparable selection from the given one be removing all non-relevant properties. Right
 * now this only is the 'formatted' value.
 *
 * @param {SelectionRecordsDto} records - The selection to reduce
 * @return {SelectionRecordsDto} The selection reduced to the properties needed for equality tests
 */
export function createComparableSelection(records: SelectionRecordsDto) {
  // Ignore additional properties that are not relevant for equality tests, e.g. 'formatted'
  return (({ dataViewId, selectionElement, definingDimensions, values }) => ({
    dataViewId,
    selectionElement,
    definingDimensions,
    values,
  }))(records);
}

/**
 * Checks whether a column is compatible to the records directly or via a semantic connection.
 *
 * @param columnIdentification - The column to check compatible
 * @param records - The records
 * @return If the column is compatible to the records
 */
export function isColumnIdentificationDirectlyOrSemanticallyCompatibleToSelection(
  columnIdentification: ColumnIdentificationDto | undefined,
  records: SelectionRecordsDto | undefined,
): boolean {
  if (!records) {
    return false;
  }
  return isDirectlyOrSemanticallyCompatibleToSelection(
    columnIdentification?.origin,
    columnIdentification?.definingDimensions.map((qco) => qco.columnOrigin),
    columnIdentification?.originSemanticSignature,
    records,
    createElementHash(records.selectionElement, records.definingDimensions),
  );
}

/**
 * Checks whether a column is compatible to the records directly or via a semantic connection.
 * This methods takes a precalculated hash to improve performance.
 *
 * @param columnIdentification - The column to check compatible
 * @param records - The records
 * @param selectionElementHash - Pre calculated hash of the records element
 * @return If the column is compatible to the records
 */
export function isColumnIdentificationDirectlyOrSemanticallyCompatibleToSelectionWithHashProvided(
  columnIdentification: ColumnIdentificationDto | undefined,
  records: SelectionRecordsDto | undefined,
  selectionElementHash: string,
): boolean {
  if (!records) {
    return false;
  }
  return isDirectlyOrSemanticallyCompatibleToSelection(
    columnIdentification?.origin,
    columnIdentification?.definingDimensions.map((qco) => qco.columnOrigin),
    columnIdentification?.originSemanticSignature,
    records,
    selectionElementHash,
  );
}

/**
 * Checks whether a column is compatible to the records directly or via a semantic connection.
 *
 * @param origin - The column origin to check compatible
 * @param definingDimensions - The column defining dimensions to check compatible
 * @param originSemanticSignature - The column semantic signature to check compatible
 * @param records - The records
 * @param selectionElementHash - Pre calculated hash of the records element
 * @return If the column is compatible to the records
 */
export function isDirectlyOrSemanticallyCompatibleToSelection(
  origin: ColumnOriginDto | undefined,
  definingDimensions: ColumnOriginDto[] | undefined,
  originSemanticSignature: string | undefined,
  records: SelectionRecordsDto | undefined,
  selectionElementHash: string,
): boolean {
  if (!definingDimensions || !origin || !records) {
    return false;
  }
  if (definingDimensions && isEqual(selectionElementHash, createElementHash(origin, definingDimensions))) {
    return true;
  }
  // Semantic compatible are only dimension selections
  if (isMeasureSelection(records)) {
    return false;
  }
  // compare semantic signatures
  return (
    !!records.selectionElementSemanticSignature &&
    !!originSemanticSignature &&
    records.selectionElementSemanticSignature === originSemanticSignature
  );
}

/**
 * Checks whether the selection is a measure selection.
 *
 * @param {SelectionRecordsDto} records - The selection
 * @return {boolean} If the selection is a measure selection
 */
function isMeasureSelection(records: SelectionRecordsDto) {
  // It is a measure selection when the selection cannot be found in the defining dimensions
  return !records.definingDimensions.some((definingDimension) => isEqual(definingDimension, records.selectionElement));
}

export function createQueryElementHash(selectionElement: ColumnOriginDto, definingDimensions: QueryColumnOriginDto[]) {
  return createElementHash(
    selectionElement,
    definingDimensions.map((qco) => qco.columnOrigin),
  );
}

export function createElementHash(selectionElement: ColumnOriginDto, definingDimensions: ColumnOriginDto[]) {
  return toHashCode([
    selectionElement.baseAttributeHash,
    ...definingDimensions.map((origin) => origin.baseAttributeHash),
  ]);
}

function createDataPointHash(
  definingDimensions: QueryColumnOriginDto[] | undefined,
  dataPoint: Record<string, unknown>,
): string | undefined {
  if (!dataPoint) {
    return;
  }
  const newSelectionValues = definingDimensions?.map((defDim) =>
    defDim.columnId ? dataPoint[defDim.columnId] : undefined,
  );
  if (!newSelectionValues) {
    return;
  }
  return toHashCode(newSelectionValues);
}

export function valuesMatch(
  definingDimensions: QueryColumnOriginDto[] | undefined,
  currentSelectionHashes: string[],
  dataPoint: Record<string, unknown>,
): boolean {
  const dataPointHash = createDataPointHash(definingDimensions, dataPoint);
  if (!dataPointHash) {
    return false;
  }
  return currentSelectionHashes.indexOf(dataPointHash) >= 0;
}

export function dataPointsMatchByHash(
  definingDimensions: QueryColumnOriginDto[],
  a: Record<string, unknown>,
  b: Record<string, unknown>,
) {
  const hashA = createDataPointHash(definingDimensions, a);
  const hashB = createDataPointHash(definingDimensions, b);
  return hashA === hashB;
}

export function cleanupStateAfterDataAcquisition(
  usedObjecttypeIdOrIds: ObjecttypeId | ObjecttypeIdString | (ObjecttypeId | ObjecttypeIdString)[],
  datasetKeysToRemoveFromSelection: DatasetKey[],
  clearSelection: boolean = false,
) {
  // Combine both actions so that we do not reload twice (once per action)
  return getStore().dispatch(async (dispatch, getState) => {
    const newSelection = createNewSelectionWithoutSpecificDatasetKeys(
      getState(),
      datasetKeysToRemoveFromSelection,
      clearSelection,
    );
    if (newSelection) {
      await dispatch(executeCommand(Command.SET_SELECTION(newSelection)));
    }
    dispatch(invalidateDataViewsUsingObjecttypes({ objecttypeIds: array(usedObjecttypeIdOrIds) }));
    await dispatch(fetchDataDependencyState());
  });
}

function createNewSelectionWithoutSpecificDatasetKeys(
  state: RootStateOr<WorkbookState>,
  datasetKeysToRemoveFromSelection: DatasetKey[],
  clearSelection: boolean = false,
): SetSelectionCommandPayload | undefined {
  if (clearSelection) {
    return {};
  }

  const { records } = getSelection(state)!;

  // Do not modify selection if there is none, or if nothing has to be removed
  if (!records || datasetKeysToRemoveFromSelection.length === 0) {
    return;
  }

  const { dataViewId, definingDimensions, selectionElement, values } = records;

  // Do not modify the selection if it is not based on the dataset key, either
  const datasetKeyAttributes = new Set(Object.keys(datasetKeysToRemoveFromSelection[0]));
  const definingDimensionAttributes = new Set(definingDimensions.map((dim) => dim.baseAttribute));
  if (!isEqual(definingDimensionAttributes, datasetKeyAttributes)) {
    return {};
  }

  const remainingValues = values.filter((primaryKeyValues) => {
    const asDatasetKey = toDatasetKey(definingDimensions, primaryKeyValues);
    return !datasetKeysToRemoveFromSelection.some((deletedDatasetKey) => isEqual(deletedDatasetKey, asDatasetKey));
  });

  // Clear selection if there are no remaining selected values
  if (remainingValues.length === 1) {
    return {};
  }

  return {
    selection: {
      dataViewId,
      definingDimensions,
      selectionElement,
      values: remainingValues,
    },
  };
}

function toDatasetKey(definingDimensions: ColumnOriginDto[], keyArray: unknown[]) {
  const datasetKey: DatasetKey = {};
  definingDimensions.forEach((dim, index) => {
    datasetKey[dim.baseAttribute] = keyArray[index] as DatasetKeyValue;
  });
  return datasetKey;
}
